import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  ArrowBack,
  ArrowDownward,
  Block,
  AssignmentReturn,
} from "@material-ui/icons";
import CommonButton from "app/common/Component/Button/CommonButton";
import { getDateTime } from "app/common/LocalFunction";
import { useStore } from "app/stores";
import { observer } from "mobx-react";
import { memo, useEffect, useState } from "react";

/**
 * Popup hoàn trả — đồng bộ UX STORE /return-request:
 * ① Chọn phiếu xuất kho → bấm「Tải thông tin」
 * ② Nhập SL hoàn theo lô + lý do
 */
const RequestStoreReturnProductsPopup = observer(() => {
  const {
    openReturnProductPopup,
    handleCloseReturnProductPopup,
    selectedReturnDrugOrder,
    returnExportTransactions,
    pickedReturnExportTx,
    selectedReturnExportTx,
    returnInfoLoaded,
    returnableLines,
    returnLoading,
    returnLinesLoading,
    returnSubmitting,
    handlePickReturnExportTx,
    handleLoadReturnExportTxInfo,
    handleBackReturnExportTxSelect,
    handleSubmitReturnProducts,
  } = useStore().patientManagementStore.registerProductStore;

  const [qtyMap, setQtyMap] = useState({});
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (openReturnProductPopup) {
      setQtyMap({});
      setReason("");
    }
  }, [openReturnProductPopup]);

  useEffect(() => {
    if (returnInfoLoaded) {
      setQtyMap({});
    }
  }, [returnInfoLoaded, selectedReturnExportTx?.storeTransactionId]);

  const lines = returnableLines || [];
  const txOptions = returnExportTransactions || [];
  const pickedId = pickedReturnExportTx?.storeTransactionId;

  const setQty = (transItemId, remaining, raw) => {
    let v = raw === "" ? "" : Number(raw);
    if (v !== "" && !Number.isNaN(v)) {
      if (v < 0) v = 0;
      if (remaining != null && v > remaining) v = remaining;
    }
    setQtyMap((prev) => ({ ...prev, [transItemId]: v }));
  };

  const handleSubmit = async () => {
    const items = lines
      .map((l) => ({
        storeTransactionId: l.storeTransactionId,
        storeTransactionCode: l.storeTransactionCode,
        transItemId: l.transItemId,
        stockUnitId: l.stockUnitId,
        storeRequestItemId: l.storeRequestItemId,
        quantity: Number(qtyMap[l.transItemId]) || 0,
      }))
      .filter((it) => it.quantity > 0);
    await handleSubmitReturnProducts({ items, reason });
  };

  const productName =
    selectedReturnDrugOrder?.drug?.registeredName ||
    selectedReturnDrugOrder?.drugName ||
    selectedReturnDrugOrder?.drug?.name ||
    "";

  return (
    <Dialog
      open={!!openReturnProductPopup}
      onClose={handleCloseReturnProductPopup}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <div className="flex align-center gap-1">
          <AssignmentReturn style={{ color: "#e67e22" }} />
          <span>Hoàn trả hàng {productName ? `— ${productName}` : ""}</span>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        {returnLoading ? (
          <div className="flex-center" style={{ padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            {/* ① Chọn phiếu xuất + Tải thông tin */}
            <Typography
              style={{
                marginBottom: 8,
                fontSize: 12,
                fontWeight: 700,
                color: "#e67e22",
                textTransform: "uppercase",
              }}
            >
              ① Phiếu xuất kho gốc
            </Typography>
            {txOptions.length === 0 ? (
              <Typography
                style={{ padding: "8px 0 16px", textAlign: "center", color: "#888" }}
              >
                Không có phiếu xuất kho nào còn hàng để hoàn.
              </Typography>
            ) : (
              <div
                style={{
                  border: "1px solid #f0c78a",
                  borderRadius: 4,
                  padding: 12,
                  marginBottom: 16,
                  background: "#fffaf3",
                }}
              >
                <div className="flex align-end gap-1" style={{ flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 280 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={48} />
                          <TableCell>Phiếu xuất</TableCell>
                          <TableCell>YCXK</TableCell>
                          <TableCell>Ngày</TableCell>
                          <TableCell align="right">Còn hoàn</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {txOptions.map((tx) => {
                          const isPicked =
                            pickedId && pickedId === tx.storeTransactionId;
                          return (
                            <TableRow
                              key={tx.storeTransactionId}
                              hover
                              selected={isPicked}
                              style={{ cursor: "pointer" }}
                              onClick={() => handlePickReturnExportTx(tx)}
                            >
                              <TableCell padding="checkbox">
                                <Radio color="primary" checked={!!isPicked} />
                              </TableCell>
                              <TableCell>
                                <b>{tx.storeTransactionCode || "—"}</b>
                              </TableCell>
                              <TableCell>{tx.storeRequestCode || "—"}</TableCell>
                              <TableCell>{getDateTime(tx.dateIssue)}</TableCell>
                              <TableCell align="right">
                                <b style={{ color: "#e67e22" }}>
                                  {tx.totalRemainingQty ?? "—"}
                                </b>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <CommonButton
                    className="btn-primary"
                    style={{ whiteSpace: "nowrap", marginBottom: 4 }}
                    disabled={!pickedReturnExportTx || returnLinesLoading}
                    onClick={handleLoadReturnExportTxInfo}
                  >
                    {returnLinesLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <ArrowDownward className="mr-1" />
                    )}
                    {returnLinesLoading ? "Đang tải…" : "Tải thông tin"}
                  </CommonButton>
                </div>
                <Typography style={{ marginTop: 8, fontSize: 11, color: "#666" }}>
                  <b>Bước 1:</b> chọn phiếu xuất → bấm <b>「Tải thông tin」</b>.
                  <b style={{ marginLeft: 8 }}>Bước 2:</b> chỉnh số lượng cần trả
                  từng dòng → Xác nhận hoàn trả.
                </Typography>
              </div>
            )}

            {/* ② Chi tiết lô — chỉ sau khi tải thông tin */}
            {returnLinesLoading && (
              <div className="flex-center" style={{ padding: 16 }}>
                <CircularProgress size={24} />
              </div>
            )}

            {!returnLinesLoading && returnInfoLoaded && (
              <>
                <Typography
                  style={{
                    marginBottom: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#2c3e50",
                    textTransform: "uppercase",
                  }}
                >
                  ② Danh sách hàng hoàn trả ({lines.length})
                </Typography>
                {lines.length === 0 ? (
                  <Typography style={{ padding: 8, color: "#888" }}>
                    Phiếu <b>{selectedReturnExportTx?.storeTransactionCode}</b>{" "}
                    không còn dòng nào để hoàn.
                  </Typography>
                ) : (
                  <>
                    <Typography style={{ marginBottom: 8, fontSize: 13, color: "#555" }}>
                      Phiếu xuất: <b>{selectedReturnExportTx?.storeTransactionCode}</b>
                      {selectedReturnExportTx?.storeRequestCode && (
                        <>
                          {" "}
                          · YCXK: <b>{selectedReturnExportTx.storeRequestCode}</b>
                        </>
                      )}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sản phẩm</TableCell>
                          <TableCell>Lô</TableCell>
                          <TableCell>ĐVT</TableCell>
                          <TableCell align="right">Đã xuất</TableCell>
                          <TableCell align="right">Đã hoàn</TableCell>
                          <TableCell align="right">Còn hoàn</TableCell>
                          <TableCell align="right" style={{ width: 120 }}>
                            SL hoàn
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lines.map((l) => (
                          <TableRow key={l.transItemId}>
                            <TableCell>{l.productName || l.productCode || "—"}</TableCell>
                            <TableCell>{l.lotNumber || l.batchCode || "—"}</TableCell>
                            <TableCell>
                              {l.stockUnitName || l.stockUnitCode || "—"}
                            </TableCell>
                            <TableCell align="right">{l.exportedQty}</TableCell>
                            <TableCell align="right">{l.returnedQty || 0}</TableCell>
                            <TableCell align="right">
                              <b style={{ color: "#e67e22" }}>{l.remainingQty}</b>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                variant="outlined"
                                value={qtyMap[l.transItemId] ?? ""}
                                onChange={(e) =>
                                  setQty(l.transItemId, l.remainingQty, e.target.value)
                                }
                                inputProps={{
                                  min: 0,
                                  max: l.remainingQty,
                                  style: { textAlign: "right" },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TextField
                      label="Lý do hoàn"
                      fullWidth
                      variant="outlined"
                      size="small"
                      style={{ marginTop: 16 }}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="VD: BN không dùng / thuốc dư / đổi phác đồ"
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {(pickedReturnExportTx || returnInfoLoaded) && txOptions.length > 0 && (
          <CommonButton className="btn-gray" onClick={handleBackReturnExportTxSelect}>
            <ArrowBack className="mr-2" /> Chọn lại
          </CommonButton>
        )}
        <CommonButton className="btn-gray" onClick={handleCloseReturnProductPopup}>
          <Block className="mr-2" /> Đóng
        </CommonButton>
        {returnInfoLoaded && lines.length > 0 && (
          <CommonButton
            onClick={handleSubmit}
            disabled={returnSubmitting}
          >
            <AssignmentReturn className="mr-2" />
            {returnSubmitting ? "Đang hoàn..." : "Xác nhận hoàn trả"}
          </CommonButton>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default memo(RequestStoreReturnProductsPopup);
